import { create } from 'zustand';
import { getDomain, clearCurrencyCache } from '@/lib/utils';
import { cacheStoreLogo, cacheStoreColors } from '@/utils/logoCache';

const applyStoreColors = (colors: {
  primary?: string;
  background?: string;
  text?: string;
  button?: string;
  buttonText?: string;
}) => {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const primary = colors.primary || '#a855f7';
  const background = colors.background || '#ffffff';
  const text = colors.text || '#333333';
  const button = colors.button || '#a855f7';
  const buttonText = colors.buttonText || '#ffffff';

  // Keep existing theme namespace for compatibility
  root.style.setProperty('--theme-color-primary', primary);
  root.style.setProperty('--theme-color-background', background);
  root.style.setProperty('--theme-color-text', text);
  root.style.setProperty('--theme-color-accent', button);
  root.style.setProperty('--theme-color-button', button);
  root.style.setProperty('--theme-color-secondary', background);
  root.style.setProperty('--theme-color-muted', text);
  root.style.setProperty('--theme-color-button-text', buttonText);

  // Also set the color variables used by frontend CSS and Tailwind dynamic theme values
  root.style.setProperty('--color-primary', primary);
  root.style.setProperty('--color-bg', background);
  root.style.setProperty('--color-text', text);
  root.style.setProperty('--color-button', button);
  root.style.setProperty('--color-button-text', buttonText);
};

const initialStoreInfo = (typeof window !== 'undefined' ? (window as any).__INITIAL_STATE__ : null) || null;

if (initialStoreInfo) {
  applyStoreColors({
    primary: initialStoreInfo.site_color || '#a855f7',
    background: initialStoreInfo.site_bg_color || '#ffffff',
    text: initialStoreInfo.site_text_color || '#333333',
    button: initialStoreInfo.site_button_color || '#a855f7',
    buttonText: initialStoreInfo.site_button_text_color || '#ffffff'
  });
}

interface StoreInfo {
  site_name: string;
  slogan: string;
  site_logo: string;
  about_us: string;
  contact_email: string;
  phone_num: string;
  shop_address: string;
  shop_ig: string;
  shop_wa: string;
  shop_tg: string;
  google_map: string;
  whatsFloat: number;
  currenCy?: string;
  currency?: string;
  currency_symbol?: string;
  checkout_note: string;
  // Bank transfer fields
  m_bank: string;
  m_ac_name: string;
  m_ac_num: string;
  mb_active: number;
  // Paystack fields
  pays_pk: string;
  pays_active: number;
  // Monnify fields
  monify_api: string;
  monify_active: number;
  // Stripe fields
  stripeActive: number;
  StripeKey: string;
  StripeSecrete: string;
  // Split payment fields
  sepShipping: number;
  sepShippingAcn: string;
  // Shipbubble fields
  shipbubble?: number;
  shipbuKeybble?: string;
  shipbubblePackagingCode?: string;
  shipbubblePickupAddressCode?: string;
  heroSlider: string[];
  site_lock?: number;
  id?: number;
  // Color theme fields
  site_color?: string;
  site_bg_color?: string;
  site_text_color?: string;
  site_button_color?: string;
  site_button_text_color?: string;
  heroLeadText?: string;
  whyChooseUsText?: string;
  returnPolicy?: string;
}

interface StoreState {
  storeInfo: StoreInfo | null;
  loading: boolean;
  setStoreInfo: (info: StoreInfo) => void;
  fetchStoreInfoByDomain: (domain: string) => Promise<void>;
  error: string | null;
  setError: (error: string | null) => void;
  // site lock modal helpers
  lockNoticeOpen: boolean;
  openLockNotice: () => void;
  closeLockNotice: () => void;
}

export const useStoreInfo = create<StoreState>((set) => ({
  storeInfo: initialStoreInfo,
  loading: initialStoreInfo ? false : true,
  error: null,
  // lock modal defaults
  lockNoticeOpen: false,
  openLockNotice: () => set({ lockNoticeOpen: true }),
  closeLockNotice: () => set({ lockNoticeOpen: false }),
  setError: (error) => set({ error }),
  setStoreInfo: (info) => {
    set({ storeInfo: info, error: null, loading: false });
    
    // Clear currency cache to force refresh on next formatPrice call
    clearCurrencyCache();
    
    // Store currency - handle both 'currency' and 'currenCy' (typo variants)
    const currency = info.currency || info.currenCy || 'NGN';
    localStorage.setItem('currency', currency);
    localStorage.setItem('currency_symbol', info.currency_symbol || currency);
    
    // Cache logo if available
    if (info.site_logo) {
      cacheStoreLogo(info.site_logo, info.site_name || 'Store');
    }
    
    // Cache colors - with purple as default
    const colors = {
      primary: (info as any).site_color || '#a855f7',
      background: (info as any).site_bg_color || '#ffffff',
      text: (info as any).site_text_color || '#333333',
      button: (info as any).site_button_color || '#a855f7',
      buttonText: (info as any).site_button_text_color || '#ffffff'
    };
    cacheStoreColors(colors.primary, colors.background, colors.text, colors.button, colors.buttonText);
    applyStoreColors(colors);
  },
  fetchStoreInfoByDomain: async (domain: string, fallbackStoreId?: string) => {
    try {
      set({ loading: true });
      if ((window as any).__INITIAL_STATE__) {
        set({ storeInfo: (window as any).__INITIAL_STATE__, loading: false });
        return (window as any).__INITIAL_STATE__;
      }

      const cachedInfo = localStorage.getItem('storeInfo');
      if (cachedInfo) {
        const parsedInfo = JSON.parse(cachedInfo);
        set({ storeInfo: parsedInfo });
      }

      const apiEndpoint = import.meta.env.VITE_API_ENDPOINT || 'https://api3.cultaz.com';
      if (!apiEndpoint) {
        throw new Error('API endpoint not configured');
      }

      const fetchUrl = (params: string) => `${apiEndpoint}/api/v1/store/store-info?${params}`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const requestStoreInfo = async (url: string) => {
        console.debug('[store] fetching store-info:', url);
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          signal: controller.signal
        });

        if (!response.ok) {
          return { fallback: true, response };
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Invalid response from server. Please try again.');
        }

        const data = await response.json();
        return { fallback: false, data };
      };

      const domainResponse = await requestStoreInfo(fetchUrl(`domain=${encodeURIComponent(domain)}`));
      let data = null;

      if (domainResponse.fallback && fallbackStoreId) {
        console.debug('[store] domain lookup failed, trying fallback storeId:', fallbackStoreId);
        const storeIdResponse = await requestStoreInfo(fetchUrl(`storeId=${encodeURIComponent(fallbackStoreId)}`));
        if (storeIdResponse.fallback) {
          throw new Error('Store not found by domain or store id.');
        }
        data = storeIdResponse.data;
      } else if (domainResponse.fallback) {
        throw new Error('Failed to connect to server. Please check your connection and try again.');
      } else {
        data = domainResponse.data;
      }

      clearTimeout(timeoutId);

      // Check if response indicates a genuine store-not-found error (not just missing optional data)
      if (data.error && data.code === 'STORE_NOT_FOUND') {
        throw new Error(data.error);
      }

      // Only require status check if it's explicitly included in the response
      if ('status' in data && !data.status) {
        if (fallbackStoreId) {
          console.debug('[store] status check failed, trying fallback storeId:', fallbackStoreId);
          const storeIdResponse = await requestStoreInfo(fetchUrl(`storeId=${encodeURIComponent(fallbackStoreId)}`));
          if (storeIdResponse.fallback) {
            throw new Error(data.message || 'Store information not found. Please try again.');
          }
          data = storeIdResponse.data;
        } else {
          throw new Error(data.message || 'Store information not found. Please try again.');
        }
      }

      // Validate that we have store data
      if (!data.store) {
        throw new Error('Invalid store data received from server.');
      }

      const storeInfo = {
        ...data.store,
        heroSlider: data.heroSlider || [],
        heroLeadText: data.store?.heroLeadText || data.store?.hero_lead_text || '',
        whyChooseUsText: data.store?.whyChooseUsText || data.store?.why_choose_us_text || '',
        returnPolicy: data.store?.returnPolicy || data.store?.return_policy || '',
      };

      console.debug('[store] store info fetched successfully:', storeInfo.site_name);
      localStorage.setItem('storeInfo', JSON.stringify(storeInfo));
      setStoreInfo(storeInfo);
      set({ error: null });

      return storeInfo;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load store information. Please refresh the page.';
      set({ error: errorMessage });
      console.error('Store info fetch error:', error);

      const cachedInfo = localStorage.getItem('storeInfo');
      if (cachedInfo) {
        const parsedInfo = JSON.parse(cachedInfo);
        set({ storeInfo: parsedInfo });
      }
    } finally {
      set({ loading: false });
    }
  }
}));

export async function fetchStoreInfo() {
  const { fetchStoreInfoByDomain } = useStoreInfo.getState();
  const domain = getDomain();
  const fallbackStoreId = localStorage.getItem('storeId') || '2';

  if (!localStorage.getItem('currency')) {
    localStorage.setItem('currency', 'NGN');
  }

  localStorage.setItem('storeId', fallbackStoreId);
  return fetchStoreInfoByDomain(domain, fallbackStoreId);
}

